import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getIframeEventEmitter } from '../utils/iframeEventEmitter';
import {
  parseIframeMessage,
  validateNavigateCommand,
  validateSetFiltersCommand,
  validateScrollCommand,
  createCommandResponse,
  filtersToUrlParams,
  buildCarRoute,
} from '../utils/iframeMessageHandler';
import { isInIframe } from '../utils/iframe';
import { getCurrentPageContext } from '../utils/pageContextParser';

/**
 * Hook для двусторонней коммуникации с parent window через postMessage API
 *
 * Слушает команды от parent window:
 * - navigate: { route?, carId?, queryParams? } - Навигация по приложению
 * - set-filters: { filters } - Применить фильтры на странице /cars
 * - get-state: {} - Запросить текущее состояние приложения
 * - scroll: { direction?, position? } - Скроллинг страницы
 *
 * Отправляет события в parent window:
 * - iframe-ready - При инициализации
 * - navigation-event - При навигации пользователя
 * - filters-applied - При применении фильтров
 * - state-response - В ответ на get-state
 * - error - При ошибках обработки команд
 */
export function useIframePostMessage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isIframe = isInIframe();

  /**
   * Обработчик команды navigate
   */
  const handleNavigateCommand = useCallback(async (data, messageId) => {
    const validation = validateNavigateCommand(data);
    if (!validation.valid) {
      console.error('[useIframePostMessage] Invalid navigate command:', validation.error);
      return createCommandResponse(messageId, false, {}, validation.error);
    }

    const { route, carId, queryParams } = data;

    try {
      let targetRoute = route;

      // Если передан carId, строим route для карточки автомобиля
      if (carId && !route) {
        targetRoute = buildCarRoute(carId);
      }

      // Строим полный URL с query параметрами
      let targetUrl = targetRoute;
      if (queryParams && Object.keys(queryParams).length > 0) {
        const params = new URLSearchParams(queryParams);
        targetUrl = `${targetRoute}?${params.toString()}`;
      }

      // Устанавливаем флаг, чтобы избежать отправки navigation-event обратно в parent
      window.__agentCommandInProgress = true;

      // Выполняем навигацию
      navigate(targetUrl);

      console.log(`[useIframePostMessage] Navigated to: ${targetUrl}`);

      // Сбрасываем флаг через небольшую задержку
      setTimeout(() => {
        // Флаг — браузерный глобал. Таймер переживает снос jsdom-окружения в
        // vitest и падает ReferenceError'ом в unhandled errors, роняя весь ран
        // при зелёных тестах (тот же класс, что был в runtimeConfig.js:1244).
        if (typeof window === 'undefined') return;
        window.__agentCommandInProgress = false;
      }, 300);

      return createCommandResponse(messageId, true, {
        route: targetRoute,
        url: targetUrl,
      });
    } catch (error) {
      window.__agentCommandInProgress = false;
      console.error('[useIframePostMessage] Navigation error:', error);
      return createCommandResponse(messageId, false, {}, error.message);
    }
  }, [navigate]);

  /**
   * Обработчик команды set-filters
   */
  const handleSetFiltersCommand = useCallback(async (data, messageId) => {
    const validation = validateSetFiltersCommand(data);
    if (!validation.valid) {
      console.error('[useIframePostMessage] Invalid set-filters command:', validation.error);
      return createCommandResponse(messageId, false, {}, validation.error);
    }

    const { filters } = data;

    try {
      // Конвертируем фильтры в URL параметры
      const params = filtersToUrlParams(filters);
      const newSearch = params.toString();

      // Устанавливаем флаг
      window.__agentCommandInProgress = true;

      // Применяем фильтры через навигацию (HashRouter)
      const currentRoute = location.pathname;
      navigate(`${currentRoute}?${newSearch}`);

      console.log(`[useIframePostMessage] Filters applied:`, filters);

      // Сбрасываем флаг
      setTimeout(() => {
        // Флаг — браузерный глобал. Таймер переживает снос jsdom-окружения в
        // vitest и падает ReferenceError'ом в unhandled errors, роняя весь ран
        // при зелёных тестах (тот же класс, что был в runtimeConfig.js:1244).
        if (typeof window === 'undefined') return;
        window.__agentCommandInProgress = false;
      }, 300);

      return createCommandResponse(messageId, true, {
        filters,
        appliedParams: newSearch,
      });
    } catch (error) {
      window.__agentCommandInProgress = false;
      console.error('[useIframePostMessage] Set filters error:', error);
      return createCommandResponse(messageId, false, {}, error.message);
    }
  }, [navigate, location.pathname]);

  /**
   * Обработчик команды get-state
   */
  const handleGetStateCommand = useCallback(async (messageId) => {
    try {
      // Используем pageContextParser для получения полного состояния
      const pageContext = getCurrentPageContext();

      console.log('[useIframePostMessage] Sending current state:', pageContext);

      return createCommandResponse(messageId, true, {
        state: pageContext,
      });
    } catch (error) {
      console.error('[useIframePostMessage] Get state error:', error);
      return createCommandResponse(messageId, false, {}, error.message);
    }
  }, []);

  /**
   * Обработчик команды scroll
   */
  const handleScrollCommand = useCallback(async (data, messageId) => {
    const validation = validateScrollCommand(data);
    if (!validation.valid) {
      console.error('[useIframePostMessage] Invalid scroll command:', validation.error);
      return createCommandResponse(messageId, false, {}, validation.error);
    }

    const { direction, position } = data;

    try {
      if (position !== undefined) {
        // Скролл к конкретной позиции
        window.scrollTo({
          top: position,
          behavior: 'smooth',
        });
      } else if (direction) {
        // Скролл по направлению
        switch (direction) {
          case 'top':
            window.scrollTo({ top: 0, behavior: 'smooth' });
            break;
          case 'bottom':
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            break;
          case 'up':
            window.scrollBy({ top: -window.innerHeight, behavior: 'smooth' });
            break;
          case 'down':
            window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
            break;
        }
      }

      console.log(`[useIframePostMessage] Scrolled: direction=${direction}, position=${position}`);

      return createCommandResponse(messageId, true, {
        direction,
        position,
        currentScrollY: window.scrollY,
      });
    } catch (error) {
      console.error('[useIframePostMessage] Scroll error:', error);
      return createCommandResponse(messageId, false, {}, error.message);
    }
  }, []);

  /**
   * Главный обработчик postMessage событий
   */
  const handlePostMessage = useCallback(async (event) => {
    // Парсим и валидируем сообщение
    const message = parseIframeMessage(event);
    if (!message) {
      return; // Невалидное сообщение, игнорируем
    }

    const { command, data, messageId } = message;
    console.log(`[useIframePostMessage] Received command: ${command}`, data);

    let response;

    // Обрабатываем команду
    switch (command) {
      case 'navigate':
        response = await handleNavigateCommand(data, messageId);
        break;

      case 'set-filters':
        response = await handleSetFiltersCommand(data, messageId);
        break;

      case 'get-state':
        response = await handleGetStateCommand(messageId);
        break;

      case 'scroll':
        response = await handleScrollCommand(data, messageId);
        break;

      default:
        console.warn(`[useIframePostMessage] Unknown command: ${command}`);
        response = createCommandResponse(
          messageId,
          false,
          {},
          `Unknown command: ${command}`
        );
    }

    // Отправляем ответ обратно в parent window
    if (response) {
      try {
        window.parent.postMessage(response, event.origin);
        console.log(`[useIframePostMessage] Response sent for command: ${command}`, response);
      } catch (error) {
        console.error('[useIframePostMessage] Failed to send response:', error);
      }
    }
  }, [handleNavigateCommand, handleSetFiltersCommand, handleGetStateCommand, handleScrollCommand]);

  /**
   * Setup: подключение слушателя postMessage и отправка iframe-ready
   */
  useEffect(() => {
    if (!isIframe) {
      console.log('[useIframePostMessage] Not in iframe context, skipping initialization');
      return;
    }

    console.log('[useIframePostMessage] Initializing iframe communication');

    // Получаем singleton emitter
    const emitter = getIframeEventEmitter({ debug: true });

    // Добавляем слушатель postMessage
    window.addEventListener('message', handlePostMessage);

    // Отправляем iframe-ready событие в parent
    emitter.emitReady();

    // Cleanup
    return () => {
      window.removeEventListener('message', handlePostMessage);
      console.log('[useIframePostMessage] Cleaned up message listener');
    };
  }, [isIframe, handlePostMessage]);
}
